import fscreen from 'fscreen';
import get from 'lodash/get';
import {
  takeLatest,
  all,
  call,
  put,
  select,
  takeEvery,
  fork,
  race,
  take,
} from 'redux-saga/effects';
import i18next from 'i18next';
import {
  loadDeviation,
  requestChangeViewMode,
  fullscreenExited,
  fullscreenEntered,
  initDeviationPage,
  focusCommentEditor,
  fetchMedia,
} from './actions';
import { PapiDeviation } from '@wix/da-papi-types';
import { init as initGruser } from '@wix/da-gruser-shared/pkg/redux/actions';
import {
  initializeThreadFromPapi,
  initializeEmptyThread,
} from '../../CommentThread/actionCreators';
import { focusEditorWithScroll } from '../../CommentEditor/redux/actionCreators';
import { normalize } from 'normalizr';
import { deviationSchema } from '../../redux/normalizr/schemas/deviation';
import { putEntities } from '../../redux/entities/actions';
import {
  getIsTheaterForced,
  getIsFullscreenAllowed,
  getBiEventData,
} from './selectors';
import { getIsLoggedIn, getViewMode } from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import ddt from '@wix/da-ddt';
import { DeviationViewMode } from '@wix/da-react-context/pkg/publicSession';
import { setViewMode } from '../../publicSession/actions';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import biLogger from '@wix/da-bi/pkg/BiLogger.logger';
import {
  getBiEventForViewChange,
  getDeviationNotificationQueue,
  getCommentableTypeForDeviationType,
  isExtendedDeviationFullyLoaded,
  getDefaultDuperbrowseStream,
} from './helpers';
import { setWatchingState } from '../../WatchButton/redux/actionCreators';
import {
  exitDuperbrowse,
  initializeDuperbrowse,
  shutdownDuperbrowse,
  unloadDuperbrowseItem,
  updateBiMetadata,
} from '../../Duperbrowse/redux/actionCreators';
import { logPageView } from '../../biLogger/redux/actions';
import { collectFave } from '../../CollectionPanel/redux/actionCreators';
import { removeQueues } from '../../Notifications/redux/actionCreators';
import { getCommentBoxId } from '../../CommentThread/helpers';
import { pushPopover } from '../../Popovers/redux/actionCreators';
import { PopoverType } from '../../Popovers/redux/types';
import { isDeviation } from '../../Duperbrowse/redux/helpers';
import {
  getCurrentBiMetadata,
  getRootStream,
} from '../../Duperbrowse/redux/selectors';
import { cacheReady } from '@wix/da-react-app/pkg/client/ResourceCache/redux/actions';
import { getDenormalizedDataEntityById } from '../../redux/entities';
import { BiEvent } from '@wix/da-bi/pkg/events';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { putErrorNotification } from '../../utils/saga';

const fsLogger = ddt.createLogger('fullscreen');

function* handleLoadDeviation({
  payload: { deviation },
}: ReturnType<typeof loadDeviation>) {
  const biMetadata = yield select(getCurrentBiMetadata, deviation.deviationId);
  const {
    view,
    eventComponent,
    deviationId,
    deviationResourceType,
    isDuperbrowse,
    contentViewStatus,
  } = yield select(getBiEventData, deviation);

  // Log new pageview for canonicals evid 9
  if (isExtendedDeviationFullyLoaded(deviation) && !isDuperbrowse) {
    yield put(
      logPageView({
        view,
        component: eventComponent,
        typeid: deviationResourceType,
        itemid: deviationId,
        is_duper: isDuperbrowse ? 1 : 0,
        content_view_status: contentViewStatus,
        metadata: biMetadata,
        devpageLoadingPhase: 'phase1-5',
      })
    );
  }

  // This is the old 99 pageview
  yield put(
    logPageView({
      view,
      component: eventComponent,
      typeid: deviationResourceType,
      itemid: deviationId,
      is_duper: isDuperbrowse ? 1 : 0,
      content_view_status: contentViewStatus,
      metadata: biMetadata,
    })
  );
}

function* handleInitDeviationPage({
  payload: { deviation, comments, gruser },
}: ReturnType<typeof initDeviationPage>) {
  const { entities } = normalize(deviation, deviationSchema);
  const isAuthorWatched = get(deviation, 'extended.isWatchingAuthor', false);
  const author = get(deviation, 'author.username', '');

  const actions: any[] = [
    put(
      comments
        ? initializeThreadFromPapi(comments)
        : initializeEmptyThread(
            deviation.deviationId,
            getCommentableTypeForDeviationType(deviation)
          )
    ),
    put(setWatchingState(author, isAuthorWatched)),
    put(putEntities({ entities })),
  ];
  if (gruser) {
    const { page: gruserPage, ...pureGruser } = gruser;
    actions.push(put(initGruser({ gruser: pureGruser, gruserPage })));
  }

  yield all(actions);
}

/** when a user clicks on 'show me anyway' from the devpage of a blurred, mature deviation,
 *  we fetch the unblurred `media` part of the deviation here. Note: this unblurs it in the
 *  normalized deviation array which means any thumb of this deviation will also be unblurred.
 */
function* handleFetchMedia({
  payload: { deviation },
}: ReturnType<typeof fetchMedia>) {
  // hack: clear old media so that there's no blured -> unblurred flashing
  const normalizedNoMedia = normalize(
    { ...deviation, media: {} },
    deviationSchema
  );
  yield put(putEntities(normalizedNoMedia));

  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url: '/media',
      params: { deviationid: deviation.deviationId },
    },
    undefined,
    'dadeviation'
  );

  if (!response || !response.media || isApiError(response)) {
    // restore the original
    yield put(putEntities(normalize(deviation, deviationSchema)));
    yield putErrorNotification(
      i18next.t('deviationPage.saga.handleFetchMedia.error')
    );
    return;
  }

  const withNewMedia = normalize(
    { ...deviation, media: response.media },
    deviationSchema
  );
  yield put(putEntities(withNewMedia));
}

function* handleUnloadDuperbrowseItem({
  payload: { deviation },
}: ReturnType<typeof unloadDuperbrowseItem>) {
  if (!deviation || !isDeviation(deviation)) {
    return;
  }
  yield put(
    removeQueues(
      queueName =>
        queueName.startsWith('deviation') &&
        queueName !== getDeviationNotificationQueue(deviation)
    )
  );
}

function handleEnterFullscreen() {
  fsLogger.log('Requesting fullscreen');
  fscreen.requestFullscreen(document.documentElement);
}

function handleExitFullscreen() {
  fsLogger.log('Requesting exit fullscreen');
  fscreen.exitFullscreen();
}

function* getNextViewMode(
  currentViewMode: DeviationViewMode,
  requestedViewMode: DeviationViewMode,
  deviation?: PapiDeviation
) {
  const isTheaterStateForced = yield select(getIsTheaterForced, deviation);

  const normalModeIfAllowed = isTheaterStateForced
    ? DeviationViewMode.Theater
    : DeviationViewMode.Normal;

  const isFullscreenAllowed = yield select(getIsFullscreenAllowed);

  let nextViewMode: DeviationViewMode | undefined;
  switch (currentViewMode) {
    case DeviationViewMode.Fullscreen:
      switch (requestedViewMode) {
        case DeviationViewMode.Fullscreen:
        case DeviationViewMode.Normal:
          nextViewMode = normalModeIfAllowed;
          break;
        case DeviationViewMode.Theater:
          nextViewMode = DeviationViewMode.Theater;
          break;
        default:
      }
      break;
    case DeviationViewMode.Theater:
      switch (requestedViewMode) {
        case DeviationViewMode.Fullscreen:
          if (isFullscreenAllowed) {
            nextViewMode = DeviationViewMode.Fullscreen;
          }
          break;
        case DeviationViewMode.Theater:
        case DeviationViewMode.Normal:
          nextViewMode = normalModeIfAllowed;
          break;
        default:
      }
      break;
    case DeviationViewMode.Normal:
      switch (requestedViewMode) {
        case DeviationViewMode.Fullscreen:
          if (isFullscreenAllowed) {
            nextViewMode = DeviationViewMode.Fullscreen;
          }
          break;
        case DeviationViewMode.Theater:
          nextViewMode = DeviationViewMode.Theater;
          break;
        default:
      }
      break;
    default:
  }

  return nextViewMode;
}

function* handleRequestChangeViewMode() {
  // Because this waits in a while loop, we dont want it on the serverside
  if (typeof window === 'undefined') {
    return;
  }
  // Things are done in a bit special way here - instead of relying on
  // takeEvery/takeLatest we roll our own event loop. This is because we
  // need to handle browser fullscreen events in different ways depending
  // on where we are in the process of changing view mode and the built in
  // functions were not adequate for this case.
  // If the user requested a change to/from fullscreen, we wait for these
  // events before changing the store to make sure by the time the selector
  // is run (which defaults to the browser's state in case of doubt), the
  // browser is in the right state already.
  // When there is no request from the user, we still listen for fullscreen
  // change events to make absolutely sure we are in sync with whatever the
  // browser is doing (eg. if the user exited fullscreen via esc key)
  // This is because not synchronizing on them can cause race conditions
  // between handling browser fullscreen change and the user's own change
  // request.
  while (true) {
    // we wait for either of 3 events here: user-requested mode changes,
    // and the browser fullscreen state changes (enter/exit)
    const {
      requestChange,
      fsExit,
      fsEnter,
    }: {
      requestChange?: ReturnType<typeof requestChangeViewMode>;
      fsExit?: ReturnType<typeof fullscreenExited>;
      fsEnter?: ReturnType<typeof fullscreenEntered>;
    } = yield race({
      requestChange: take(requestChangeViewMode),
      fsExit: take(fullscreenExited),
      fsEnter: take(fullscreenEntered),
    });

    // determine what the target view mode is based on what we got
    const requestedViewMode = requestChange
      ? requestChange.payload.viewMode
      : fsExit
        ? DeviationViewMode.Normal
        : fsEnter
          ? DeviationViewMode.Fullscreen
          : undefined;

    const deviation: PapiDeviation | undefined = get(
      requestChange || fsExit || fsEnter || {},
      'payload.deviation'
    );

    // this should not happen(tm), but just in case
    if (!requestedViewMode) {
      continue;
    }

    fsLogger.log('View mode change requested:', requestedViewMode);

    // now figure out what our _actual_ next mode will be
    const currentViewMode = yield select(getViewMode);
    const nextViewMode: DeviationViewMode | undefined = yield call(
      getNextViewMode,
      currentViewMode,
      requestedViewMode,
      deviation
    );

    fsLogger.log('Determined target view mode:', nextViewMode);

    // nothing changed, go back to waiting for events
    if (currentViewMode === nextViewMode) {
      continue;
    }

    // for anything involving fullscreen, we need to synchronize on browser events
    // except if the change was triggered by the browser event to begin with
    if (currentViewMode === DeviationViewMode.Fullscreen && !fsExit) {
      fsLogger.log('Full screen exit sync start');
      yield all([call(handleExitFullscreen), take(fullscreenExited)]);
      fsLogger.log('Full screen exit sync complete');
    } else if (nextViewMode === DeviationViewMode.Fullscreen && !fsEnter) {
      fsLogger.log('Full screen exit sync start');
      yield all([call(handleEnterFullscreen), take(fullscreenEntered)]);
      fsLogger.log('Full screen enter sync complete');
    }

    // if all is settled, time to change the store and back to listening for new events
    if (nextViewMode && deviation) {
      fsLogger.log('Mode change ready', nextViewMode);
      const { view, publicSession, eventComponent, deviationId } = yield select(
        getBiEventData,
        deviation
      );

      const event: BiEvent = getBiEventForViewChange(
        currentViewMode,
        nextViewMode,
        {
          view,
          component: eventComponent,
          deviationid: deviationId,
          viewmode: nextViewMode as any,
        }
      );

      yield all([
        put(setViewMode({ currentViewMode, nextViewMode })),
        call([biLogger, 'log'], publicSession, event),
      ]);
    }
  }
}

function handleExitDuperbrowse() {
  if (fscreen.fullscreenElement) {
    fscreen.exitFullscreen();
  }
}

function* handleCollectFave({
  payload: { notificationQueue, actionBarType },
}: ReturnType<typeof collectFave>) {
  if (!notificationQueue.startsWith('deviation')) {
    return;
  }

  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.ADD_TO_GROUP })
    );
    return;
  }

  yield put(
    pushPopover(
      actionBarType === 'primary'
        ? PopoverType.ADD_TO_COLLECTION_PRIMARY
        : PopoverType.ADD_TO_COLLECTION_SECONDARY,
      {
        params: { ignorePreselectedCollections: true },
      }
    )
  );
}

function* handleFocusCommentEditor({ payload: { deviation } }) {
  const { deviationId } = deviation;

  const editorId = getCommentBoxId(
    getCommentableTypeForDeviationType(deviation),
    deviationId,
    'top'
  );

  yield put(focusEditorWithScroll(editorId, true));
}

function* handleResourceCacheReady({
  payload: { name, data },
}: ReturnType<typeof cacheReady>) {
  if (name === 'relatedContent') {
    // update entity
    const deviationEntity: PapiDeviation = yield select(
      getDenormalizedDataEntityById,
      deviationSchema,
      data.__cacheExtra.deviationEntityId
    );

    if (deviationEntity?.extended?.relatedContent) {
      ddt.log(
        'deviation',
        'Skipped populating related content from cache as already present'
      );
      return;
    }

    // Update the noramlized deviation
    const updatedDeviation: PapiDeviation = {
      ...deviationEntity,
      extended: {
        ...(deviationEntity.extended ?? {}),
        relatedContent: data.relatedContent,
      },
    };
    const { entities } = normalize(updatedDeviation, deviationSchema);
    yield put(putEntities({ entities }));

    // Setup the root duperbrowse stream
    const rootStream = yield select(getRootStream);
    if (rootStream && rootStream.items.length <= 1) {
      const defaultStream = getDefaultDuperbrowseStream(updatedDeviation);
      yield put(
        initializeDuperbrowse({
          initialStream: defaultStream,
        })
      );
    }

    // Bi Metadata handling, neeed to store it and then use it
    yield put(updateBiMetadata(data.pageBiMetadata ?? '', deviationEntity));
    const biMetadata = yield select(
      getCurrentBiMetadata,
      deviationEntity.deviationId
    );
    const {
      view,
      eventComponent,
      deviationId,
      deviationResourceType,
      isDuperbrowse,
      contentViewStatus,
    } = yield select(getBiEventData, deviationEntity);

    yield put(
      logPageView({
        view,
        component: eventComponent,
        typeid: deviationResourceType,
        itemid: deviationId,
        is_duper: isDuperbrowse ? 1 : 0,
        content_view_status: contentViewStatus,
        devpageLoadingPhase: 'phase2',
        metadata: biMetadata,
      })
    );
  }
}

export default function* deviationPageSaga() {
  yield all([
    fork(handleRequestChangeViewMode),
    takeLatest(loadDeviation, handleLoadDeviation),
    takeEvery(initDeviationPage, handleInitDeviationPage),
    takeEvery(fetchMedia, handleFetchMedia),
    takeEvery(unloadDuperbrowseItem, handleUnloadDuperbrowseItem),
    takeLatest(collectFave, handleCollectFave),
    takeEvery([exitDuperbrowse, shutdownDuperbrowse], handleExitDuperbrowse),
    takeEvery(focusCommentEditor, handleFocusCommentEditor),
    takeEvery(cacheReady, handleResourceCacheReady),
  ]);
}
